import { IonModal } from '@ionic/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import DialogLoader from '../components/PageLoader/DialogLoader';
import clsx from 'clsx';

type InitialValueType = {
  openDialog: (
    node: ReactNode,
    option?: { allowBackgroundDismiss: boolean }
  ) => void;
  closeDialog: (option?: { keepParent: boolean }) => void;
  setBusy: (isBusy: boolean, note?: string) => void;
};

const DialogContext = createContext<InitialValueType | null>(null);

export const useDialog = () => {
  const context = useContext(DialogContext)!;
  return context;
};

type LoaderStatesType = {
  isBusy: boolean;
  note?: string;
};

const initialLoadingState: LoaderStatesType = {
  isBusy: false,
  note: undefined,
};

export const DialogProvider = ({ children }: React.PropsWithChildren) => {
  const location = useLocation();
  const [canDismissWithBg, setCanDismissWithBg] = useState(true);
  const [dialogNode, setDialogNode] = useState<ReactNode[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loaderStates, setLoaderStates] =
    useState<LoaderStatesType>(initialLoadingState);

  // OPEN DIALOG
  const openDialog = useCallback(
    (
      node: ReactNode,
      option: { allowBackgroundDismiss: boolean } = {
        allowBackgroundDismiss: true,
      }
    ) => {
      setDialogNode((prevNodes) => [...prevNodes, node]);
      setIsOpen(true);
      setCanDismissWithBg(option?.allowBackgroundDismiss);
    },
    []
  );

  // CLOSE DIALOG
  const closeDialog = useCallback(
    (option?: { keepParent: boolean }) => {
      setDialogNode((prevNodes) => {
        if (loaderStates.isBusy) return prevNodes;

        if (option?.keepParent && prevNodes.length > 1) {
          // Keep all but the last dialog
          return prevNodes.slice(0, -1);
        }

        setIsOpen(false);
        return prevNodes;
      });
    },
    [loaderStates.isBusy]
  );

  // SET BUSY
  const setBusy = useCallback((isBusy: boolean, note?: string) => {
    setLoaderStates({
      isBusy,
      note,
    });
  }, []);

  // Reset on modal dismiss
  const handleDialogDidDismiss = () => {
    setLoaderStates(initialLoadingState);
    setDialogNode([]);
  };

  // Close when route changes
  useEffect(() => {
    closeDialog();
  }, [location.pathname]);

  const loaderStyle = clsx(
    'absolute top-0 left-0 flex items-center w-full h-full z-50'
  );
  const contentStyle = clsx(
    'p-5 md:p-8 w-full transition-all duration-0 max-h-[calc(100vh-15vh)] dashboard-scroller overflow-y-auto',
    loaderStates.isBusy && 'opacity-0'
  );
  const dialogStyle = clsx(loaderStates.isBusy && 'active');

  const contextValue = useMemo(
    () => ({
      openDialog,
      closeDialog,
      setBusy,
    }),
    [openDialog, closeDialog, setBusy]
  );

  return (
    <DialogContext.Provider value={contextValue}>
      <>
        {children}
        {dialogNode.map((content, idx) => (
          <IonModal
            id="dialog-context"
            key={idx}
            className={dialogStyle}
            isOpen={isOpen}
            onDidDismiss={handleDialogDidDismiss}
            backdropDismiss={
              !loaderStates.isBusy && canDismissWithBg && dialogNode.length < 2
            }
            canDismiss={!loaderStates.isBusy}
          >
            {loaderStates.isBusy && (
              <div className={loaderStyle}>
                <DialogLoader note={loaderStates.note} />
              </div>
            )}
            <div className={contentStyle}>{content}</div>
          </IonModal>
        ))}
      </>
    </DialogContext.Provider>
  );
};
